import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form as FormMik } from 'formik';
import { Form, Button, Modal, Image } from 'react-bootstrap';
import * as Yup from 'yup';
import { FiCheckCircle, FiPlus, FiXCircle } from 'react-icons/fi';
import ListColor from '../components/ListColor';
import List from '../components/List/List';
import ModalMessage from '../components/ModalMessage';
import ModalYesNo from '../components/ModalYesNo';
import { useToast } from '../hooks/toast';
import IUserDepartament from '../interfaces/IUserDepartament';

import api from '../services/apiClient';
import PencilIcon from '../assets/icon/pencil.svg';
import DeleteIcon from '../assets/icon/delete.svg';

interface Departamento {
  seqDepartamento: number;
  nome: string;
  exibeChatCliente: boolean;
  ordemApresentacao: number;
  ativo: boolean;
  cor: string;
  corFundoLinha: boolean;
}

const stickyHeaderCellStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: '#D9D9D9',
};

const extractApiMessage = (data: any): string => {
  if (typeof data === 'string') return data;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.Message === 'string') return data.Message;
  return '';
};

const shouldOfferDisable = (message: string): boolean => {
  if (!message) return false;

  const normalized = message
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  return (
    normalized.includes('existem mensagens vinculadas') &&
    normalized.includes('deseja desativa')
  );
};

const Departament: React.FC = () => {
  const { addToast } = useToast();
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [dataLoad, setDataLoad] = useState<Departamento>({} as Departamento);
  const [showMsgExistsOrder, setShowMsgExistsOrder] = useState(false);
  const [showMsgLimpaDados, setShowMsgLimpaDados] = useState(false);
  const [showMsgDelete, setShowMsgDelete] = useState(false);
  const [showMsgDisable, setShowMsgDisable] = useState(false);
  const [disableMessage, setDisableMessage] = useState('');
  const [confirmAddItem, setConfirmAddItem] = useState(false);
  const [colorDepartment, setColorDepartment] = useState('#616161');
  const [registerSelected, setRegisterSelected] = useState(0);
  const [usersDepartament, setUsersDepartament] = useState<IUserDepartament[]>(
    []
  );
  const [newDepartament, setNewDepartament] = useState(false);

  const bntCadastrar = useRef<HTMLButtonElement>(null);

  const colors = [
    '#009F23',
    '#008BEF',
    '#FFC107',
    '#EA00EF',
    '#1300EF',
    '#DE0000',
  ];

  const inputNomeDepartamento = useRef<HTMLInputElement>(null);

  const token = localStorage.getItem('@LiveChat:token');

  const initialValues = {
    seqDepartamento: 0,
    nome: '',
    exibeChatCliente: true,
    ordemApresentacao: 0,
    ativo: true,
    cor: '#616161',
    corFundoLinha: false,
  };

  const loadDepartamentos = (): void => {
    api
      .get('api/departamento', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          for (let row = 0; row < result.data.length; row += 1) {
            result.data[row].exibeChatCliente =
              result.data[row].exibeChatCliente === 'S';
            result.data[row].ativo = result.data[row].ativo === 'S';
            result.data[row].corFundoLinha =
              result.data[row].corFundoLinha === 'S';
          }

          setDepartamentos(result.data);
        }
      });
  };

  const loadDepartamento = (id: number): void => {
    api
      .get(`api/departamento/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          result.data.exibeChatCliente = result.data.exibeChatCliente === 'S';
          result.data.ativo = result.data.ativo === 'S';
          result.data.corFundoLinha = result.data.corFundoLinha === 'S';

          setDataLoad(result.data);
          if (bntCadastrar && bntCadastrar.current) {
            bntCadastrar.current.innerText = 'Atualizar';
          }
        } else {
          addToast({
            type: 'error',
            title: 'Erro',
            description: result.message,
          });
        }
      });
  };

  useEffect(() => {
    loadDepartamentos();
    setDataLoad(initialValues);
  }, []);

  const validationSchemaEmpresa = Yup.object().shape({
    nome: Yup.string()
      .required('Nome do departamento é obrigatório')
      .max(40, 'Nome do departamento deve ter no máximo 40 caracteres'),
    ordemApresentacao: Yup.number()
      .required('Ordem de apresentação é obrigatório')
      .min(0, 'Ordem de apresentação não pode ser um número negativo.')
      .max(999, 'Ordem de apresentação deve ter o valor maximo de 999.'),
  });

  const handlerSaveUserDepartament = async (
    dataUsersDepartament: any
  ): Promise<void> => {
    try {
      let requestValue: any = null;

      if (usersDepartament.some((dpt) => dpt.seqDepartamento === 0)) {
        const updatedGroups = usersDepartament.map((dpt) => ({
          ...dpt,
          id: dpt.seqUsuario,
          seqDepartamento: dataUsersDepartament.seqDepartamento,
        }));
        setUsersDepartament(updatedGroups);
        requestValue = updatedGroups;
      } else {
        requestValue = usersDepartament;
      }

      const response = await api.post('/DepartamentoUsuario', requestValue, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Permissão de departamento gravado com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao gravar permissão de departamento.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    }
  };

  const loadAllUsers = (): void => {
    api
      .get('api/User', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const usersDepartamentDb: IUserDepartament[] = [];

        response.data.data
          .sort((a: any, b: any) => a.nome.localeCompare(b.nome))
          .forEach((item: any) => {
            usersDepartamentDb.push({
              seqDepartamento: 0,
              nomeDepartamento: '',
              seqUsuario: item.seqUsuario,
              nomeUsuario: item.nome,
              membroDepartamento: 'N',
            });
          });

        setUsersDepartament(usersDepartamentDb);
      });
  };

  const handleSubmit = (values: Departamento): void => {
    try {
      const data = {
        seqDepartamento: values.seqDepartamento,
        nome: values.nome,
        exibeChatCliente: values.exibeChatCliente ? 'S' : 'N',
        ordemApresentacao: values.ordemApresentacao,
        ativo: values.ativo ? 'S' : 'N',
        cor: colorDepartment === '#616161' ? null : colorDepartment,
        corFundoLinha: values.corFundoLinha ? 'S' : 'N',
      };

      if (dataLoad.seqDepartamento <= 0) {
        const existsOrder = departamentos.findIndex(
          (item) => item.ordemApresentacao === data.ordemApresentacao
        );

        if (existsOrder >= 0 && !confirmAddItem) {
          setDataLoad(values);
          setShowMsgExistsOrder(true);
          return;
        }

        api
          .post('api/departamento', data, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((response) => {
            const result = response.data;
            if (result.success) {
              handlerSaveUserDepartament(result.data);
              loadDepartamentos();
              addToast({
                type: 'success',
                title: 'Sucesso',
                description: 'Cadastrado com sucesso',
              });
              setDataLoad(initialValues);
              setConfirmAddItem(false);
            } else {
              const checkValues = values;
              setDataLoad(checkValues);
              addToast({ title: result.message, type: 'error' });
            }
          })
          .catch((error) => {
            console.log('error', error);
          });
      } else {
        const existsOrder = departamentos.findIndex(
          (item) =>
            item.ordemApresentacao === data.ordemApresentacao &&
            item.seqDepartamento !== data.seqDepartamento
        );

        if (existsOrder >= 0 && !confirmAddItem) {
          setDataLoad(values);
          setShowMsgExistsOrder(true);
          return;
        }

        api
          .put('/api/departamento', data, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((response) => {
            const result = response.data;
            if (result.success) {
              handlerSaveUserDepartament(result.data);
              loadDepartamentos();
              addToast({ title: 'Atualizado com sucesso', type: 'success' });
              setConfirmAddItem(false);
            } else {
              const checkValues = values;
              setDataLoad(checkValues);
              addToast({ title: result.message, type: 'error' });
            }
          })
          .catch((error) => {
            console.log('error', error);
          });
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao cadastrar grupo',
      });
    } finally {
      setNewDepartament(false);
      setDataLoad(initialValues);
      loadAllUsers();
    }
  };

  const loadDepartamentsUsers = async (
    seqDepartamento: number
  ): Promise<void> => {
    const response = await api.get(
      `/DepartamentoUsuario/AllUsers/${seqDepartamento}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setUsersDepartament(response.data.data);
  };

  const handleConfirmAddItem = (): void => {
    setConfirmAddItem(true);
    setShowMsgExistsOrder(false);
    if (bntCadastrar && bntCadastrar.current) {
      bntCadastrar.current.click();
    }
  };

  const handleUpdateUserDepartament = (
    seqUsuario: number,
    membroDepartamento: boolean
  ): void => {
    setUsersDepartament((records) => [
      ...records.map((record) => {
        if (record.seqUsuario === seqUsuario) {
          return {
            ...record,
            membroDepartamento: membroDepartamento ? 'S' : 'N',
          };
        }
        return record;
      }),
    ]);
  };

  const handleCleanData = (): void => {
    setShowMsgLimpaDados(false);
    if (dataLoad.seqDepartamento > 0) setNewDepartament(false);
    setDataLoad({
      ...dataLoad,
      nome: '',
      exibeChatCliente: true,
      ordemApresentacao: 0,
      ativo: true,
      cor: '#616161',
      corFundoLinha: false,
    });
    setNewDepartament(false);
    loadAllUsers();
    setColorDepartment('');
    inputNomeDepartamento.current?.focus();
  };

  const handleDeleteDepartment = async (): Promise<void> => {
    try {
      const response = await api.delete(
        `/api/departamento/${registerSelected}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const apiMessage = extractApiMessage(response?.data);

      if (response?.status >= 400 && shouldOfferDisable(apiMessage)) {
        setDisableMessage(apiMessage);
        setShowMsgDisable(true);
        setShowMsgDelete(false);
        return;
      }

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Departamento excluído com sucesso',
          type: 'success',
        });
        loadDepartamentos();
      } else {
        if (shouldOfferDisable(apiMessage)) {
          setDisableMessage(apiMessage);
          setShowMsgDisable(true);
          setShowMsgDelete(false);
          return;
        }

        addToast({
          title: 'Atenção',
          description:
            apiMessage || response.data.message || response.data.Message,
          type: 'error',
        });
      }
    } catch (err: any) {
      const apiMessage = extractApiMessage(err?.response?.data);

      if (shouldOfferDisable(apiMessage)) {
        setDisableMessage(apiMessage);
        setShowMsgDisable(true);
      } else {
        addToast({
          title: 'Atenção',
          description: apiMessage || err?.message,
          type: 'error',
        });
      }
    }

    setShowMsgDelete(false);
  };

  const handleDisableDepartment = async (): Promise<void> => {
    try {
      const responseDepartamento = await api.get(
        `api/departamento/${registerSelected}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const departamento = responseDepartamento.data.data;
      const payload = {
        seqDepartamento: departamento.seqDepartamento,
        nome: departamento.nome,
        exibeChatCliente: departamento.exibeChatCliente,
        ordemApresentacao: departamento.ordemApresentacao,
        ativo: 'N',
        cor: departamento.cor,
        corFundoLinha: departamento.corFundoLinha,
      };

      const response = await api.put('/api/departamento', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Departamento desativado com sucesso.',
          type: 'success',
        });
        loadDepartamentos();
      } else {
        addToast({
          title: 'Erro',
          description: response.data.message || response.data.Message,
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err?.message,
        type: 'error',
      });
    } finally {
      setShowMsgDisable(false);
    }
  };

  return (
    <>
      <ModalMessage
        title="Limpar dados do departamento"
        message="Isso irá limpar os dados do departamento selecionado. Gostaria de limpar todos os dados?"
        handleConfirme={handleCleanData}
        showMessage={showMsgLimpaDados}
        setShowMessage={setShowMsgLimpaDados}
      />

      <ModalMessage
        title="Atenção"
        message="Já existe um departamento cadastrado com a ordem de apresentação
        informada. Deseja continuar e alterar todos?"
        handleConfirme={handleConfirmAddItem}
        showMessage={showMsgExistsOrder}
        setShowMessage={setShowMsgExistsOrder}
      />

      <ModalMessage
        title="Excluir departamento"
        message="Isso irá excluir o departamento selecionado. Gostaria de excluí-lo?"
        handleConfirme={handleDeleteDepartment}
        showMessage={showMsgDelete}
        setShowMessage={setShowMsgDelete}
      />

      <ModalYesNo
        title="Atenção"
        message={disableMessage}
        show={showMsgDisable}
        onYes={handleDisableDepartment}
        onNo={() => setShowMsgDisable(false)}
      />

      <div className="d-flex">
        <section className="col-6">
          <div
            className="card position-static ms-2"
            style={{
              borderRadius: '0.68rem',
              height: 'Calc(100vh - 16rem)',
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div
              className="table-responsive overflow-hidden h-100"
              style={{
                borderRadius: '0.68rem',
                borderColor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between m-0 pe-2 ps-2 text-white"
                style={{
                  fontWeight: '600',
                  fontSize: '1.7rem',
                  backgroundColor: '#1A1A1A',
                }}
              >
                <span className="pb-1 pt-1 d-flex align-items-center justify-content-start">
                  Departamentos
                </span>
                <Button
                  variant="primary"
                  onClick={() => {
                    setDataLoad({
                      seqDepartamento: 0,
                      nome: '',
                      ativo: true,
                      cor: '',
                      exibeChatCliente: false,
                      ordemApresentacao: 0,
                      corFundoLinha: false,
                    });
                    loadAllUsers();
                    setColorDepartment('');
                    setNewDepartament(true);
                    inputNomeDepartamento.current?.focus();
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FiPlus style={{ height: '1.38rem', width: '1.38rem' }} />
                    <span className="ms-1">adicionar</span>
                  </div>
                </Button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <table className="table table-nowrap mb-0 table-striped-custom">
                  <thead
                    className="small fw-light text-dark"
                    style={{
                      backgroundColor: '#D9D9D9',
                      borderRadius: '0.68rem!important',
                      fontSize: '1.25rem',
                    }}
                  >
                    <tr style={{ color: '#000' }}>
                      <th className="col-4 ms-3" style={stickyHeaderCellStyle}>
                        Nome do Grupo
                      </th>
                      <th className="col-3" style={stickyHeaderCellStyle}>
                        Cor de Fundo
                      </th>
                      <th className="col-1" style={stickyHeaderCellStyle}>
                        Exibir
                      </th>
                      <th className="col-1" style={stickyHeaderCellStyle}>
                        Ordem
                      </th>
                      <th className="col-1" style={stickyHeaderCellStyle}>
                        Ativo
                      </th>
                      <th className="col-1" style={stickyHeaderCellStyle}>
                        {' '}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {departamentos
                      .sort((a, b) => a.nome.localeCompare(b.nome))
                      .map((departamento) => (
                        <tr
                          key={departamento.seqDepartamento}
                          style={{
                            color: '#000',
                            fontSize: '1.25rem',
                            fontWeight: '600',
                          }}
                        >
                          <td className="col-4">
                            <span>{departamento.nome}</span>
                          </td>
                          <td className="col-3">
                            <div className="d-flex align-items-center justify-content-start">
                              <div
                                style={{
                                  height: '2rem',
                                  width: '2rem',
                                  backgroundColor: `${
                                    departamento.cor === null
                                      ? '#616161'
                                      : departamento.cor
                                  }`,
                                }}
                                className=" rounded-circle"
                              />
                              <span className="ms-2">{departamento.cor}</span>
                            </div>
                          </td>
                          <td className="col-1 text-center">
                            <span>
                              {departamento.exibeChatCliente ? 'Sim' : 'Não'}
                            </span>
                          </td>
                          <td className="col-1 text-center">
                            <span>{departamento.ordemApresentacao}</span>
                          </td>
                          <td className="col-1">
                            <div className="d-flex align-items-center justify-content-center pt-2">
                              {departamento.ativo ? (
                                <FiCheckCircle
                                  style={{
                                    color: 'green',
                                    height: '1.8rem',
                                    width: '1.8rem',
                                  }}
                                />
                              ) : (
                                <FiXCircle
                                  style={{
                                    color: 'red',
                                    height: '1.8rem',
                                    width: '1.8rem',
                                  }}
                                />
                              )}
                            </div>
                          </td>
                          <td className="col-1">
                            <div className="d-flex justify-content-end me-2">
                              <Button
                                variant="primary m-0 p-1"
                                onClick={() => {
                                  setDataLoad(departamento);
                                  setColorDepartment(departamento.cor);
                                  loadDepartamentsUsers(
                                    departamento.seqDepartamento
                                  );
                                  setNewDepartament(false);
                                  inputNomeDepartamento.current?.focus();
                                }}
                              >
                                <Image
                                  src={PencilIcon}
                                  alt="Editar"
                                  style={{ height: '1.4rem', width: '1.5rem' }}
                                />
                              </Button>
                              <Button variant="secondary" className="ms-2 p-1">
                                <Image
                                  src={DeleteIcon}
                                  alt="Deletar"
                                  style={{ height: '1.4rem', width: '1.5rem' }}
                                  onClick={() => {
                                    setRegisterSelected(
                                      departamento.seqDepartamento
                                    );
                                    setShowMsgDelete(true);
                                  }}
                                />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="col-6">
          <div
            className="card position-static ms-2"
            style={{
              borderRadius: '0.68rem',
              height: 'Calc(100vh - 16rem)',
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
              opacity: `${
                !dataLoad.seqDepartamento && !newDepartament ? '0.5' : '1'
              }`,
            }}
          >
            <div
              className="table-responsive overflow-hidden ms-2 mt-1"
              style={{
                borderRadius: '0.68rem',
                borderColor: 'transparent',
                display: 'flex flex-row',
                pointerEvents: `${
                  !dataLoad.seqDepartamento && !newDepartament ? 'none' : 'auto'
                }`,
              }}
            >
              <div className="mt-3 me-2 ms-1">
                <Formik
                  initialValues={dataLoad || initialValues}
                  validationSchema={validationSchemaEmpresa}
                  enableReinitialize
                  onSubmit={(values, { setSubmitting, resetForm }) => {
                    setSubmitting(true);
                    handleSubmit(values);
                    resetForm();
                    setSubmitting(false);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    isSubmitting,
                    resetForm,
                  }) => (
                    <FormMik>
                      <Form.Group controlId="nome" className="mb-2">
                        <Form.Label
                          style={{ fontSize: '1.25rem', fontWeight: '600' }}
                        >
                          Nome do Departamento
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Nome"
                          name="nome"
                          ref={inputNomeDepartamento}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.nome}
                          className={`inputBox ${
                            touched.nome && errors.nome ? 'is-invalid' : ''
                          }`}
                        />
                        {touched.nome && errors.nome ? (
                          <div className="invalid-feedback">{errors.nome}</div>
                        ) : null}
                      </Form.Group>

                      <div className="row">
                        <Form.Group
                          controlId="ordemApresentacao"
                          className="mb-2 col-6"
                        >
                          <Form.Label
                            style={{ fontSize: '1.25rem', fontWeight: '600' }}
                          >
                            Ordem de exibição
                          </Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Ordem de exibição"
                            name="ordemApresentacao"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.ordemApresentacao}
                            className={`inputBox ${
                              touched.ordemApresentacao &&
                              errors.ordemApresentacao
                                ? 'is-invalid'
                                : ''
                            }`}
                          />
                          {touched.ordemApresentacao &&
                          errors.ordemApresentacao ? (
                            <div className="invalid-feedback">
                              {errors.ordemApresentacao}
                            </div>
                          ) : null}
                        </Form.Group>

                        <div className="col-6">
                          <div>
                            <Form.Check
                              type="checkbox"
                              style={{
                                fontSize: '1.25rem',
                                fontWeight: '600',
                              }}
                              name="corFundoLinha"
                              label="Cor de fundo da linha"
                              className="input-CheckBox mb-2"
                              checked={values.corFundoLinha}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <div id="divColorPicker">
                              <ListColor
                                colors={colors}
                                colorSelected={colorDepartment}
                                setColorSelected={setColorDepartment}
                                enable={
                                  !!dataLoad.seqDepartamento || newDepartament
                                }
                                positionShow="2rem"
                                showColorSelected
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <Form.Check
                        type="checkbox"
                        style={{ fontSize: '1.25rem', fontWeight: '600' }}
                        name="exibeChatCliente"
                        label="Exibe o Departamento na abertura do atendimento"
                        className="input-CheckBox"
                        checked={values.exibeChatCliente}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />

                      <Form.Check
                        type="checkbox"
                        style={{ fontSize: '1.25rem', fontWeight: '600' }}
                        name="ativo"
                        label="Ativo"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        checked={values.ativo}
                        className="my-2 input-CheckBox"
                      />

                      <div
                        className="table-responsive overflow-hidden"
                        style={{
                          borderRadius: '0.68rem',
                          borderColor: 'transparent',
                        }}
                      >
                        <div
                          className="w-100 text-white p-2"
                          style={{ backgroundColor: '#1A1A1A' }}
                        >
                          <span
                            style={{ fontSize: '1.25rem', fontWeight: '600' }}
                          >
                            Usuários vinculados ao departamento
                          </span>
                        </div>
                        <div
                          className="rounded w-100 border-0 overflow-auto"
                          style={{
                            height: '18rem',
                          }}
                        >
                          <List
                            data={usersDepartament}
                            handleUpdateRegister={handleUpdateUserDepartament}
                            idField="seqUsuario"
                            nameField="nomeUsuario"
                            checkedField="membroDepartamento"
                          />
                        </div>
                      </div>

                      <div className="d-flex justify-content-center ms-1 mt-2 p-2">
                        <Button
                          type="button"
                          className="bg-white border-white"
                          style={{
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            color: '#616161',
                            boxShadow: '0px 3px 4px -1px rgba(0, 0, 0, 0.25)',
                            borderRadius: '0.6875rem',
                            height: '2.8rem',
                          }}
                          onClick={() => {
                            setShowMsgLimpaDados(true);
                          }}
                        >
                          limpar dados
                        </Button>
                        <Button
                          variant="primary"
                          type="submit"
                          className="ms-4"
                          disabled={isSubmitting}
                          style={{
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            height: '2.8rem',
                          }}
                        >
                          confirmar
                        </Button>
                      </div>
                    </FormMik>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Departament;
